import type { AssetId, AssetRendererProps } from "@hoylu/client-common";
import { clsx } from "clsx";
import debug from "debug";
import React, { type CSSProperties, useState } from "react";
import useAsyncEffect from "use-async-effect";
import { useWorkspaceDefaultImage } from "../../utils/defaultImages";
import { useI18n } from "../../utils/hooks/use.i18n";
import Styles from "./WorkspaceTemplateCard.module.css";

const trace = debug("hoylu:dashboard:workspace-template-card");

const truncateStyles: CSSProperties = {
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 4,
  WebkitBoxOrient: "vertical",
};

export type TemplateAsset = {
  templateName: string;
  templateDescription?: string;
  fetchThumbnail?: () => Promise<Response>;
  onCreate?: () => void;
  onPreview?: () => void;
} & AssetId;

export const WorkspaceTemplateCard: React.FC<
  AssetRendererProps<TemplateAsset>
> = ({
  asset: {
    templateName,
    templateDescription,
    fetchThumbnail,
    onCreate,
    onPreview,
  },
}) => {
  const t = useI18n("WORKSPACE_TEMPLATES.");

  const defaultImgSrc = useWorkspaceDefaultImage("HOYLU");

  const [showCreate, setShowCreate] = useState(false);
  const [imgSrc, setImgSrc] = useState<string>(defaultImgSrc);

  useAsyncEffect(
    async (isStillMounted) => {
      if (!fetchThumbnail) return;

      try {
        // fetching here to nicely handle 404's and avoid situation when default thumbnails are blinking
        const response = await fetchThumbnail();
        if (response.status !== 200 || !isStillMounted()) return;
        const blob = await response.blob();
        if (isStillMounted() && blob) setImgSrc(URL.createObjectURL(blob));
      } catch (err) {
        trace("Error fetching thumbnail", { error: err });
      }
    },
    [fetchThumbnail],
  );

  return (
    <div
      className={Styles.workspaceTemplateCard}
      onClick={() => setShowCreate(!showCreate)}
    >
      <div
        className={clsx(
          Styles.animate,
          showCreate ? Styles.invisible : Styles.visible,
          Styles.thumbnail,
        )}
        onClick={(e) => {
          if (onPreview) {
            e.stopPropagation();
          }
        }}
      >
        {!!onPreview && (
          <Tile
            className={clsx(Styles.preview, Styles.animate)}
            icon="hoylu-ui-icons-indicator-show"
            label={t("PREVIEW")}
            onClick={onPreview}
          />
        )}
        <img
          alt={templateName}
          className={Styles.templateImage}
          src={imgSrc}
          title={templateName}
        />
      </div>

      <div className={Styles.template}>
        <div className={Styles.templateName} title={templateName}>
          {templateName}
        </div>
        <div
          className={Styles.templateDescription}
          style={truncateStyles}
          title={templateDescription}
        >
          {templateDescription}
        </div>
      </div>
      <Tile
        className={clsx(
          Styles.createWorkspace,
          Styles.animate,
          showCreate ? Styles.visible : Styles.invisible,
        )}
        icon="hoylu-ui-icons-quill"
        label={t("CREATE")}
        onClick={onCreate}
      />
    </div>
  );
};

type TileProps = {
  icon: string;
  label: string;
  onClick?: () => void;
  className?: string;
};

const Tile = ({ icon, label, onClick, className = "" }: TileProps) => {
  return (
    <div
      className={clsx(Styles.tile, className)}
      onClick={(e) => {
        onClick?.();
        e.stopPropagation();
      }}
      title={label}
    >
      <div className={clsx(Styles.tileIcon, icon)} />
      <div className={Styles.tileLabel}>{label}</div>
    </div>
  );
};
